import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../edit/editApi';
import { getTodayStr, getYesterdayStr, isYmd } from '../edit/editCalc';
import { MIN_REPORT_DATE } from '../summary/summaryApi';
import { fetchDaySums, fetchTerminals } from '../delivery/deliveryApi';
import type { CityTerminal, DaySums } from '../delivery/deliveryApi';
import {
  buildComparison,
  buildExportRows,
  buildTerminalIndex,
  summarizeSales
} from '../delivery/deliveryCalc';
import type { SheetTable } from '../delivery/deliveryCalc';
import { exportDeliveryExcel, readDeliveryFile } from '../delivery/deliveryExcel';

export type FileKind = 'sales' | 'archive';

export interface FileSlotState {
  name: string;
  table: SheetTable | null;
  loading: boolean;
  error: string;
}

interface DayState {
  data: DaySums | null;
  loading: boolean;
  error: string;
}

const EMPTY_SLOT: FileSlotState = { name: '', table: null, loading: false, error: '' };

/** Задержка перед запросом: пока оператор набирает дату вручную, промежуточные значения не уходят на сервер */
const DATE_DEBOUNCE_MS = 300;

/** Дата подходит для запроса: полная, не раньше начала отчетов и не из будущего */
function isRequestableDate(value: string): boolean {
  return isYmd(value) && value >= MIN_REPORT_DATE && value <= getTodayStr();
}

export function useCompare() {
  const [terminals, setTerminals] = useState<CityTerminal[] | null>(null);
  const [terminalsError, setTerminalsError] = useState('');

  const [files, setFiles] = useState<Record<FileKind, FileSlotState>>({ sales: EMPTY_SLOT, archive: EMPTY_SLOT });
  const [date, setDate] = useState(getYesterdayStr);
  const [day, setDay] = useState<DayState>({ data: null, loading: isRequestableDate(getYesterdayStr()), error: '' });
  const [dayNonce, setDayNonce] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Номера последних запросов: ответ устаревшего запроса не должен перекрыть новый (и не пишется после ухода с экрана)
  const terminalsRequest = useRef(0);
  const fileRequest = useRef<Record<FileKind, number>>({ sales: 0, archive: 0 });

  useEffect(() => {
    const requests = fileRequest.current;
    return () => {
      terminalsRequest.current += 1;
      requests.sales += 1;
      requests.archive += 1;
    };
  }, []);

  const loadTerminals = useCallback(async (silent: boolean) => {
    const requestNo = ++terminalsRequest.current;
    try {
      const list = await fetchTerminals();
      if (requestNo !== terminalsRequest.current) return;
      setTerminals(list);
      setTerminalsError('');
    } catch (error) {
      if (requestNo !== terminalsRequest.current) return;
      const message = apiErrorMessage(error, 'Не удалось загрузить данные о городах');
      // Обновление после правок в окне городов не должно затирать уже показанный список
      if (silent) notify.error(message);
      else setTerminalsError(message);
    }
  }, []);

  useEffect(() => {
    loadTerminals(false);
  }, [loadTerminals]);

  const retryTerminals = useCallback(() => {
    setTerminalsError('');
    loadTerminals(false);
  }, [loadTerminals]);

  // Суммы доставки за выбранный день
  useEffect(() => {
    if (!isRequestableDate(date)) {
      setDay({ data: null, loading: false, error: '' });
      return;
    }

    let cancelled = false;
    // Прежние суммы остаются на экране (приглушенными), пока не придут новые
    setDay((prev) => ({ ...prev, loading: true, error: '' }));

    const timer = setTimeout(async () => {
      try {
        const data = await fetchDaySums(date);
        if (!cancelled) setDay({ data, loading: false, error: '' });
      } catch (error) {
        if (cancelled) return;
        setDay({ data: null, loading: false, error: apiErrorMessage(error, 'Не удалось получить суммы доставки за день') });
      }
    }, DATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [date, dayNonce]);

  const retryDay = useCallback(() => setDayNonce((n) => n + 1), []);

  // Города и терминалы изменились: список терминалов и суммы по аптекам нужно обновить
  const handleTerminalsChanged = useCallback(() => {
    loadTerminals(true);
    setDayNonce((n) => n + 1);
  }, [loadTerminals]);

  const selectFile = useCallback(async (kind: FileKind, file: File) => {
    const requestNo = ++fileRequest.current[kind];
    setFiles((prev) => ({ ...prev, [kind]: { name: file.name, table: null, loading: true, error: '' } }));

    try {
      const table = await readDeliveryFile(file, kind);
      if (requestNo !== fileRequest.current[kind]) return;
      setFiles((prev) => ({ ...prev, [kind]: { name: file.name, table, loading: false, error: '' } }));
    } catch (error) {
      if (requestNo !== fileRequest.current[kind]) return;
      const message = apiErrorMessage(error, 'Не удалось прочитать файл');
      setFiles((prev) => ({ ...prev, [kind]: { name: file.name, table: null, loading: false, error: message } }));
    }
  }, []);

  const clearFile = useCallback((kind: FileKind) => {
    // Читающийся в этот момент файл тоже отменяется
    fileRequest.current[kind] += 1;
    setFiles((prev) => ({ ...prev, [kind]: EMPTY_SLOT }));
  }, []);

  const index = useMemo(() => buildTerminalIndex(terminals || []), [terminals]);

  const summary = useMemo(() => {
    const sales = files.sales.table;
    if (!sales || !terminals) return null;
    return summarizeSales(sales, files.archive.table, index);
  }, [files.sales.table, files.archive.table, terminals, index]);

  const comparison = useMemo(
    () => buildComparison(terminals || [], summary, day.data),
    [terminals, summary, day.data]
  );

  const canDownload = !!files.sales.table && !!files.archive.table && !!terminals && !exporting;

  const download = useCallback(async () => {
    const sales = files.sales.table;
    const archive = files.archive.table;
    if (!sales || !archive || !terminals) return;

    setExporting(true);
    try {
      await exportDeliveryExcel(sales.headers, buildExportRows(sales, archive, index));
    } catch (error) {
      notify.error(apiErrorMessage(error, 'Не удалось сформировать файл'));
    } finally {
      setExporting(false);
    }
  }, [files.sales.table, files.archive.table, terminals, index]);

  return {
    terminals,
    terminalsError,
    retryTerminals,
    handleTerminalsChanged,

    files,
    selectFile,
    clearFile,

    date,
    setDate,
    day,
    retryDay,

    summary,
    comparison,
    canDownload,
    exporting,
    download
  };
}
